"use client";
import { motion } from "framer-motion";
import { useState } from "react";

// Clean color palette - Only Deep Teal Green and White
const colors = {
  primary: "#395750",      // Deep Teal Green - Company color
  primaryDark: "#2A423C",  // Darker teal
  primaryLight: "#4B6B63", // Lighter teal
  white: "#FFFFFF",        // White
  whiteOff: "#F8F9FA",     // Off-white for subtle contrast
  textDark: "#1A2E29",     // Dark text for contrast
};

const MissionVision = () => {
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);

  // YEGNA Core Values
  const coreValues = [
    { letter: "Y", title: "Yielding Impact" },
    { letter: "E", title: "Excellence" },
    { letter: "G", title: "Growth" },
    { letter: "N", title: "Nobility" },
    { letter: "A", title: "Adaptability" },
  ];

  return (
    <section
      className="relative w-full overflow-hidden py-16 shadow-lg transition-all duration-300"
      style={{
        backgroundColor: colors.white,
        boxShadow: `0px 0px 40px ${colors.primary}20`,
      }}
    >
      {/* Geometric Background Elements */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute left-1/4 top-20 h-64 w-64 rounded-full blur-3xl" style={{ backgroundColor: `${colors.primary}10` }} />
        <div className="absolute bottom-20 right-1/4 h-48 w-48 rounded-full blur-3xl" style={{ backgroundColor: `${colors.primary}20` }} />
      </div>

      <div className="container mx-auto max-w-7xl px-4">
        <div className="mb-16 text-center">
          <motion.h2
            initial={{ opacity: 1, y: 0 }}
            className="mt-0 text-center text-4xl font-bold sm:text-5xl"
          >
            <span className="relative inline-block">
              <span className="bg-gradient-to-r bg-clip-text text-transparent" style={{ backgroundImage: `linear-gradient(to right, ${colors.primary}, ${colors.primaryLight})` }}>
                Our Vision and Mission
              </span>
              <span className="absolute bottom-0 left-0 h-1 w-full bg-gradient-to-r" style={{ background: `linear-gradient(to right, ${colors.primary}, ${colors.primaryLight})` }}></span>
            </span>
          </motion.h2>
          <div
            className="mx-auto mt-4 h-1 w-24 rounded-full"
            style={{ background: `linear-gradient(to right, ${colors.primary}, ${colors.primaryLight})` }}
          />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="grid grid-cols-1 gap-16 md:grid-cols-2"
        >
          {/* Vision Card */}
          <div
            className="relative rounded-2xl border p-10 backdrop-blur-md transition-all duration-500 hover:shadow-xl hover:scale-105"
            style={{
              borderColor: colors.primary,
              backgroundColor: hoveredCard === 'vision' ? colors.white : colors.primary
            }}
            onMouseEnter={() => setHoveredCard('vision')}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <div className="mb-6">
              <h3 className="font-orbitron text-2xl transition-colors duration-300" style={{ color: hoveredCard === 'vision' ? colors.primary : colors.white }}>
                Our Vision
              </h3>
              <div className="mt-2 h-[2px] w-16" style={{ background: `linear-gradient(to right, ${colors.primaryLight}, ${colors.primary})` }} />
            </div>
            <p className="font-poppins leading-relaxed text-xl transition-colors duration-300" style={{ color: hoveredCard === 'vision' ? colors.primary : colors.white }}>
              To be one of the leading and inclusive Microfinance in Ethiopia by 2030.
            </p>
          </div>

          {/* Mission Card */}
          <div
            className="relative rounded-2xl border p-10 backdrop-blur-md transition-all duration-500 hover:shadow-xl hover:scale-105"
            style={{
              borderColor: colors.primary,
              backgroundColor: hoveredCard === 'mission' ? colors.white : colors.primary
            }}
            onMouseEnter={() => setHoveredCard('mission')}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <div className="mb-6">
              <h3 className="font-orbitron text-2xl transition-colors duration-300" style={{ color: hoveredCard === 'mission' ? colors.primary : colors.white }}>
                Our Mission
              </h3>
              <div className="mt-2 h-[2px] w-16" style={{ background: `linear-gradient(to right, ${colors.primaryLight}, ${colors.primary})` }} />
            </div>
            <p className="font-poppins leading-relaxed text-xl transition-colors duration-300" style={{ color: hoveredCard === 'mission' ? colors.primary : colors.white }}>
              Committed to empower Women, Youth, Micro, Small and Medium Enterprises by providing sustainable, fast, most preferred and customer based Micro Financing Services through the expertise of qualified professionals and up-to-date technology.
            </p>
          </div>
        </motion.div>

        {/* ==================== REDESIGNED CORE VALUES (YEGNA) ==================== */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true, margin: "50px" }}
          className="mt-24"
        >
          {/* Single YEGNA Card - White by default, teal on hover */}
          <div
            className="relative mx-auto max-w-4xl overflow-hidden rounded-3xl border shadow-2xl transition-all duration-500 hover:shadow-2xl hover:scale-[1.01]"
            style={{
              backgroundColor: hoveredCard === 'coreValues' ? colors.primary : colors.white,
              borderColor: colors.primary,
            }}
            onMouseEnter={() => setHoveredCard('coreValues')}
            onMouseLeave={() => setHoveredCard(null)}
          >
            {/* Pattern Overlay (subtle dots) */}
            <div
              className="pointer-events-none absolute inset-0 transition-opacity duration-500"
              style={{
                opacity: hoveredCard === 'coreValues' ? 0.10 : 0.06,
                backgroundImage: `radial-gradient(circle at 20% 30%, ${colors.primary} 1.5px, transparent 1.5px),
                                  radial-gradient(circle at 70% 60%, ${colors.primary} 1.5px, transparent 1.5px),
                                  radial-gradient(circle at 40% 80%, ${colors.primary} 1.5px, transparent 1.5px)`,
                backgroundSize: "80px 80px",
              }}
            />

            <div className="relative z-10 px-6 py-12 sm:px-12 sm:py-16">
              {/* Header with Icon */}
              <div className="mb-10 flex items-center justify-center gap-4">
                <div
                  className="flex h-16 w-16 items-center justify-center rounded-full border-2 transition-colors duration-500"
                  style={{ borderColor: hoveredCard === 'coreValues' ? colors.white : colors.primary }}
                >
                  {/* Diamond / Gem Icon */}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={hoveredCard === 'coreValues' ? colors.white : colors.primary}
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-8 w-8 transition-colors duration-500"
                  >
                    <path d="M6 3h12l4 6-10 12L2 9l4-6z" />
                    <path d="M2 9h20" />
                    <path d="M6 3l4 6" />
                    <path d="M18 3l-4 6" />
                    <path d="M12 21l-4-12" />
                    <path d="M12 21l4-12" />
                  </svg>
                </div>
                <h3
                  className="font-orbitron text-3xl font-bold tracking-wider transition-colors duration-500 sm:text-4xl"
                  style={{ color: hoveredCard === 'coreValues' ? colors.white : colors.primary }}
                >
                  CORE VALUES
                </h3>
              </div>

              {/* Values List */}
              <div className="mx-auto max-w-md space-y-4">
                {coreValues.map((value, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.1 * index }}
                    viewport={{ once: true }}
                    className="flex items-center gap-3"
                  >
                    <span
                      className="font-orbitron text-2xl font-extrabold transition-colors duration-500 sm:text-3xl"
                      style={{ color: hoveredCard === 'coreValues' ? colors.white : colors.primary }}
                    >
                      {value.letter}
                    </span>
                    <span
                      className="font-orbitron text-2xl font-extrabold transition-colors duration-500 sm:text-3xl"
                      style={{ color: hoveredCard === 'coreValues' ? colors.white : colors.primary }}
                    >
                      -
                    </span>
                    <span
                      className="font-orbitron text-xl font-semibold tracking-wide transition-colors duration-500 sm:text-2xl"
                      style={{ color: hoveredCard === 'coreValues' ? colors.white : colors.primary }}
                    >
                      {value.title}
                    </span>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Decorative corner circle (bottom right) */}
            <div
              className="absolute bottom-6 right-6 h-8 w-8 rounded-full border-2 transition-colors duration-500"
              style={{ borderColor: hoveredCard === 'coreValues' ? `${colors.white}80` : `${colors.primary}60` }}
            />
          </div>
        </motion.div>
        {/* ==================== END REDESIGNED CORE VALUES ==================== */}
      </div>

      {/* Connecting Line */}
      <div
        className="absolute left-1/2 top-1/3 hidden h-36 w-[3px] -translate-y-1/2 md:block"
        style={{ background: `linear-gradient(to bottom, transparent, ${colors.primary}, transparent)` }}
      />

      {/* Grid Pattern Overlay */}
      <div
        className="absolute inset-0 -z-10 opacity-30"
        style={{
          backgroundImage: `linear-gradient(to right, ${colors.primary}05 1px, transparent 1px), linear-gradient(to bottom, ${colors.primary}05 1px, transparent 1px)`,
          backgroundSize: "24px 24px"
        }}
      />
    </section>
  );
};

export default MissionVision;